import React from 'react';
import Typography from '@common_typography';
import Show from '@common_show';
import Button from '@common_button';
import Image from 'next/image';
import ShareIcon from '@heroicons/react/24/outline/ShareIcon';
import XMarkIcon from '@heroicons/react/24/solid/XMarkIcon';
import useMediaQuery from '@hook/useMediaQuery';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import { getHost } from '@helper_config';
import { useRouter } from 'next/router';
import Dialog from '@common/Dialog';
import Popover from '@common_popover';
import { COLORS } from '@theme_vars';
import UrbanIcon from '@core_modules/theme/plugins/urbanicon';

const isMobileOrTablet = () => {
    if (typeof navigator !== 'undefined') {
        return /(android|iphone|ipad|mobile)/i.test(navigator.userAgent);
    }
    return false;
};
const whatsappLink = (text) => {
    const prefixUrlWA = (isMobileOrTablet() ? 'api' : 'web');
    return `https://${prefixUrlWA}.whatsapp.com/send?text=${text}`;
};

const facebookLink = (text) => `https://www.facebook.com/sharer/sharer.php?u=${text}`;

const twitterLink = (text) => `https://twitter.com/share?text=${text}`;

const Share = ({
    showLabel = true,
    whatsapp = true,
    facebook = true,
    twitter = true,
    size = 'sm',
    label,
}) => {
    const { t } = useTranslation(['common', 'product']);
    const route = useRouter();
    const { isMobile } = useMediaQuery();
    const labelFinal = label || `${t('product:shareTitle')}`;
    const linkShare = getHost() + route.asPath;
    const [showShareBottom, setShowShareBottom] = React.useState(false);

    let iconSize = 12;

    if (size === 'md') {
        iconSize = 14;
    }

    if (size === 'lg') {
        iconSize = 14;
    }

    const [open, setOpen] = React.useState(false);

    const [copied, setCopied] = React.useState(false);
    const handleCopyLink = () => {
        const textToCopy = `${getHost()}${route.asPath}`; // Get the full URL

        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                setCopied(true);
                // setTimeout(() => setCopied(false), 2000); // Optional: Show copied message for 2 seconds
                if (isMobile) {
                    window.toastMessage({
                        open: true,
                        text: 'Disalin ke Clipboard',
                        variant: 'success',
                    });
                }
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('Failed to copy:', err);
            });
    };

    const ShareContent = () => (
        <>
            <div
                className={cx(
                    'py-[15px]', 'px-[20px]', 'min-h-fit', 'max-w-[308px]', '!rounded-[5px]', 'popover-share-active',
                    'w-max',
                )}
            >
                <div className={cx('share-action', 'flex items-center', 'gap-[10px]')}>
                    <Show when={whatsapp}>
                        <Button
                            link={whatsappLink(linkShare)}
                            linkTarget="_blank"
                            variant="plain"
                            iconOnly
                            icon={(
                                <UrbanIcon
                                    fontSize={30}
                                    width={30}
                                    height={30}
                                    iconContent='"\e97a"'
                                />
                            )}
                            className="!p-0 swift-action-share-wa w-[30px]"
                            iconProps={{
                                className: '!text-neutral-400 group-hover:!text-primary',
                            }}
                        />
                    </Show>
                    <Show when={facebook}>
                        <Button
                            link={facebookLink(linkShare)}
                            linkTarget="_blank"
                            variant="plain"
                            iconOnly
                            icon={(
                                <UrbanIcon
                                    fontSize={30}
                                    width={30}
                                    height={30}
                                    iconContent='"\e97d"'
                                />
                            )}
                            className="!p-0 swift-action-share-fb w-[30px]"
                            iconProps={{
                                className: '!text-neutral-400 group-hover:!text-primary',
                            }}
                        />
                    </Show>
                    <Show when={twitter}>
                        <Button
                            link={twitterLink(linkShare)}
                            linkTarget="_blank"
                            variant="plain"
                            iconOnly
                            icon={(
                                <Image
                                    src="/assets/img/logo_x.svg"
                                    width={14}
                                    height={14}
                                    style={{ width: 14, height: 14 }}
                                    alt="image share twitter"
                                />
                            )}
                            className={cx(
                                '!p-0 swift-action-share-twitter w-[30px] h-[30px] ',
                                'border-neutral-400 border-[1px] rounded-full flex items-center justify-center',
                                'relative top-[3px] hover:border-primary',
                            )}
                            iconProps={{
                                className: '!text-neutral-400 group-hover:!text-primary',
                            }}
                        />
                    </Show>
                    <Button
                        variant="plain"
                        onClick={handleCopyLink}
                        iconOnly
                        icon={(
                            <UrbanIcon
                                fontSize={30}
                                width={30}
                                height={30}
                                iconContent='"\e979"'
                            />
                        )}
                        className="!p-0 swift-action-link w-[30px]"
                        iconProps={{
                            className: '!text-neutral-400 group-hover:!text-primary',
                        }}
                    />
                </div>
            </div>
            <style jsx>
                {`
                    .popover-share-active:after {
                        content: '';
                        background: #fff;
                        border-bottom: 1px solid ${COLORS.neutral[100]};
                        border-right: 1px solid ${COLORS.neutral[100]};
                        position: absolute;
                        left: 50px;
                        top: -7px;
                        width: 14px;
                        height: 14px;
                        transform: rotate(225deg);
                    }
                    @media(min-width:768px and max-width: 1023px) {
                        .popover-share-active:after {
                            left: 20px;
                            top: -7px;
                        }
                    }
                `}
            </style>
        </>
    );

    if (isMobile) {
        return (
            <>
                <Button
                    variant="plain"
                    icon={false}
                    iconOnly={false}
                    className="!p-0 whitespace-nowrap"
                    onClick={() => setShowShareBottom(true)}
                >
                    <Typography color="text-neutral-500 hover:text-neutral-400 flex items-center" variant="bd-2a">
                        <ShareIcon className="h-[20px] w-[20px]" />
                    </Typography>
                </Button>

                <Dialog
                    onClose={() => setShowShareBottom()}
                    useCloseTitleButton
                    closeOnBackdrop
                    closeOutsideContent
                    open={showShareBottom}
                    variant="plain"
                    classWrapper="w-full bottom-0 translate-y-0 translate-x-0 !left-0"
                    onClickClose={() => setShowShareBottom(false)}
                >
                    <div className="share-bottom-sheet p-[16px] absolute bg-neutral-white w-full bottom-0 rounded-t-[8px]">
                        <div className="share-bottom-sheet-title flex justify-between items-center">
                            <Typography variant="bd-1">{t('common:label:shareToYourFriend')}</Typography>
                            <Button
                                variant="plain"
                                className="!p-0"
                                onClick={() => setShowShareBottom(false)}
                            >
                                <XMarkIcon className="h-[20px] w-[20px]" />
                            </Button>
                        </div>
                        <div className="share-bottom-sheet-content mt-[16px]">
                            <Show when={whatsapp}>
                                <Button
                                    link={whatsappLink(linkShare)}
                                    linkTarget="_blank"
                                    variant="plain"
                                    className="flex justify-center items-center border border-neutral-200 px-[18px] py-[10px] mb-[16px]"
                                >
                                    <UrbanIcon
                                        fontSize={22}
                                        width={22}
                                        height={22}
                                        iconContent='"\e97a"'
                                    />
                                    <Typography variant="bd-2a" className="ml-[6px]">WhatsApp</Typography>
                                </Button>
                            </Show>
                            <Show when={facebook}>
                                <Button
                                    link={facebookLink(linkShare)}
                                    linkTarget="_blank"
                                    variant="plain"
                                    className="flex justify-center items-center border border-neutral-200 px-[18px] py-[10px] mb-[16px]"
                                >
                                    <UrbanIcon
                                        fontSize={22}
                                        width={22}
                                        height={22}
                                        iconContent='"\e97d"'
                                    />
                                    <Typography variant="bd-2a" className="ml-[6px]">Facebook</Typography>
                                </Button>
                            </Show>
                            <Show when={twitter}>
                                <Button
                                    link={twitterLink(linkShare)}
                                    linkTarget="_blank"
                                    variant="plain"
                                    className="flex justify-center items-center border border-neutral-200 px-[18px] py-[10px] mb-[16px]"
                                >
                                    <div className="border border-primary rounded-full w-[22px] h-[22px] flex items-center justify-center">
                                        <Image
                                            src="/assets/img/logo_x.svg"
                                            width={iconSize}
                                            height={iconSize}
                                            style={{ width: iconSize, height: iconSize }}
                                            alt="image share twitter mobile"
                                        />
                                    </div>
                                    <Typography variant="bd-2a" className="ml-[6px]">Twitter</Typography>
                                </Button>
                            </Show>
                            <Button
                                onClick={handleCopyLink}
                                variant="plain"
                                className="flex justify-center items-center border border-neutral-200 px-[18px] py-[10px] mb-[16px] w-full"
                            >
                                <UrbanIcon
                                    fontSize={22}
                                    width={22}
                                    height={22}
                                    iconContent='"\e979"'
                                />
                                <Typography variant="bd-2a" className="ml-[6px]">Copy Link</Typography>
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </>
        );
    }

    const handleClose = () => {
        setCopied(false);
    };

    return (
        <>
            <Dialog
                open={copied}
                closeOnBackdrop
                closeOutsideContent
                onClose={handleClose}
                classContainer=" relative"
                classWrapperTitle="!rounded-none !pt-0 pb-[0px] flex-col-reverse"
                classContent="!rounded-none !px-5 !pb-[30px] !pt-4"
                className="animate-anima-slide-down"
                classWrapper="!m-0"
                backdropClassName="!m-0"
                useCloseTitleButton={false}
                content={(
                    <>
                        <div className="relative flex justify-end">
                            <Button
                                onClick={handleClose}
                                iconOnly
                                className={
                                    cx(
                                        'swift-button-close-dialog',
                                        '!py-2 !px-0 -mr-1 relative',
                                    )
                                }
                                variant="plain"
                                icon={<XMarkIcon className="h-[24] w-[24]" />}
                            />
                        </div>
                        <div className="dialog-content block">
                            <Typography variant="p-2a" className="!text-[13px] !font-normal !leading-normal">
                                Disalin ke Clipboard
                            </Typography>
                        </div>
                        <div className="relative flex justify-end mt-2">
                            <Button variant="plain" className="!p-0" onClick={handleClose}>
                                Close
                            </Button>
                        </div>
                    </>
                )}
            />
            <div className={cx('share')}>
                <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} className={cx('share-items inline-block')}>
                    <Popover
                        content={<ShareContent />}
                        open={open}
                        setOpen={setOpen}
                        className={cx(
                            'top-[99%]',
                            'left-[0%]',
                            'w-auto',
                        )}
                        contentClassName={cx('!rounded-[5px] !border-neutral-100 shadow-type-1 max-h-fit')}
                    >
                        <Show when={showLabel}>
                            <div className={cx('share-label flex items-center gap-[8px] cursor-pointer hover:underline !text-neutral-400')}>
                                <ShareIcon className="h-[20px] w-[20px] !text-neutral-400" />
                                <Typography variant="bd-2b" className="!text-neutral-400">
                                    {labelFinal}
                                </Typography>
                            </div>
                        </Show>
                    </Popover>
                </div>
            </div>
        </>
    );
};

export default Share;
